import { LevelMap, GRID_W, GRID_H } from '../types';

// Layout legend:
// '#' hard block (indestructible)
// '.' empty
// 's' soft block (destructible)
// '1' P1 spawn
// '2' P2 spawn

function generateClassicLayout(softDensity: number, seed: number): string[] {
  const rows: string[] = [];
  for (let r = 0; r < GRID_H; r++) {
    let line = '';
    for (let c = 0; c < GRID_W; c++) {
      const onBorder = r === 0 || c === 0 || r === GRID_H - 1 || c === GRID_W - 1;
      const onPillar = r % 2 === 0 && c % 2 === 0;
      const isP1Spawn = r === 1 && c === 1;
      const isP2Spawn = r === GRID_H - 2 && c === GRID_W - 2;
      const isSpawnAdj =
        (r === 1 && c === 2) || (r === 2 && c === 1) ||
        (r === GRID_H - 2 && c === GRID_W - 3) || (r === GRID_H - 3 && c === GRID_W - 2);

      if (onBorder || onPillar) line += '#';
      else if (isP1Spawn) line += '1';
      else if (isP2Spawn) line += '2';
      else if (isSpawnAdj) line += '.';
      else {
        const v = (r * 31 + c * 17 + seed * 53) % 100;
        line += v < softDensity * 100 ? 's' : '.';
      }
    }
    rows.push(line);
  }
  return rows;
}

// Returns true when (r,c) should be a hard pillar in a boss arena.
// variant 1-5, two worlds per variant (Math.ceil(world/2)).
function bossPillar(variant: number, r: number, c: number): boolean {
  const midR = Math.floor(GRID_H / 2); // 6
  const midC = Math.floor(GRID_W / 2); // 7
  switch (variant) {
    case 1: // corner pillars (worlds 1-2)
      return (r === 3 && c === 3) || (r === 3 && c === GRID_W - 4) ||
             (r === GRID_H - 4 && c === 3) || (r === GRID_H - 4 && c === GRID_W - 4);
    case 2: // offset columns — two asymmetric 3-tall columns left/right
      return (c === 4 && (r === 4 || r === 5 || r === 6)) ||
             (c === GRID_W - 5 && (r === 6 || r === 7 || r === 8));
    case 3: // center plus sign — connects via outer corridors
      return (r === midR && c >= 5 && c <= GRID_W - 6) ||
             (c === midC && r >= 4 && r <= GRID_H - 5);
    case 4: // four L-shapes in quadrant corners
      return (r === 3 && c === 4) || (r === 4 && c === 4) ||
             (r === 3 && c === GRID_W - 5) || (r === 4 && c === GRID_W - 5) ||
             (r === GRID_H - 4 && c === 4) || (r === GRID_H - 5 && c === 4) ||
             (r === GRID_H - 4 && c === GRID_W - 5) || (r === GRID_H - 5 && c === GRID_W - 5);
    case 5: // dual horizontal barriers with gaps at center
      return (r === midR - 1 && (c === midC - 2 || c === midC || c === midC + 2)) ||
             (r === midR + 1 && (c === midC - 2 || c === midC || c === midC + 2));
    default:
      return false;
  }
}

function generateBossLayout(world: number): string[] {
  const variant = Math.ceil(world / 2); // 1-5
  const rows: string[] = [];
  for (let r = 0; r < GRID_H; r++) {
    let line = '';
    for (let c = 0; c < GRID_W; c++) {
      const onBorder = r === 0 || c === 0 || r === GRID_H - 1 || c === GRID_W - 1;
      const isP1Spawn = r === 1 && c === 1;
      const isP2Spawn = r === GRID_H - 2 && c === GRID_W - 2;
      // Keep spawn corners free of obstacles
      const spawnSafe = (r <= 2 && c <= 2) || (r >= GRID_H - 3 && c >= GRID_W - 3);
      if (onBorder) { line += '#'; continue; }
      if (isP1Spawn) { line += '1'; continue; }
      if (isP2Spawn) { line += '2'; continue; }
      if (spawnSafe) { line += '.'; continue; }
      line += bossPillar(variant, r, c) ? '#' : '.';
    }
    rows.push(line);
  }
  return rows;
}

type StageIdx = 0 | 1 | 2 | 3 | 4; // index into per-world 5-element tuple (stage 1..5)
type StageTuple<T> = readonly [T, T, T, T, T];

// Per-world tuning. Density rises, time falls, enemy count climbs.
interface WorldTuning {
  density: StageTuple<number>;
  enemies: StageTuple<number>;
  timeLimit: StageTuple<number>;
  themeName: string;
}

const WORLD_TUNINGS: Record<number, WorldTuning> = {
  1:  { density: [0.50, 0.55, 0.55, 0.60, 0.60], enemies: [2, 3, 4, 4, 5], timeLimit: [9999, 9999, 9999, 9999, 9999], themeName: 'Câmpia Verde' },
  2:  { density: [0.55, 0.55, 0.60, 0.60, 0.65], enemies: [3, 4, 4, 5, 5], timeLimit: [9999, 9999, 9999, 9999, 9999], themeName: 'Peștera Cristalină' },
  3:  { density: [0.55, 0.60, 0.60, 0.65, 0.65], enemies: [3, 4, 5, 5, 6], timeLimit: [9999, 9999, 9999, 9999, 9999], themeName: 'Pădurea Întunecată' },
  4:  { density: [0.60, 0.60, 0.65, 0.65, 0.70], enemies: [4, 5, 5, 6, 6], timeLimit: [9999, 9999, 9999, 9999, 9999], themeName: 'Castelul de Foc' },
  5:  { density: [0.55, 0.60, 0.60, 0.65, 0.65], enemies: [4, 5, 6, 6, 7], timeLimit: [9999, 9999, 9999, 9999, 9999], themeName: 'Insula Plutitoare' },
  6:  { density: [0.60, 0.65, 0.65, 0.70, 0.70], enemies: [5, 5, 6, 7, 7], timeLimit: [9999, 9999, 9999, 9999, 9999], themeName: 'Mlaștina Otrăvii' },
  7:  { density: [0.60, 0.65, 0.65, 0.70, 0.70], enemies: [5, 6, 6, 7, 7], timeLimit: [9999, 9999, 9999, 9999, 9999], themeName: 'Orașul Ruinat' },
  8:  { density: [0.65, 0.65, 0.70, 0.70, 0.70], enemies: [6, 6, 7, 7, 8], timeLimit: [9999, 9999, 9999, 9999, 9999], themeName: 'Tundra de Gheață' },
  9:  { density: [0.65, 0.70, 0.70, 0.70, 0.70], enemies: [6, 7, 7, 8, 8], timeLimit: [9999, 9999, 9999, 9999, 9999], themeName: 'Vulcanul Activ' },
  10: { density: [0.70, 0.70, 0.70, 0.70, 0.70], enemies: [7, 7, 8, 8, 8], timeLimit: [9999, 9999, 9999, 9999, 9999], themeName: 'Castelul Pieros' },
};

function makeLevel(world: number, stage: number): LevelMap {
  const t = WORLD_TUNINGS[world];
  if (stage === 6) {
    return {
      world, stage,
      layout: generateBossLayout(world),
      enemyCount: 0,
      timeLimit: Math.max(120, t.timeLimit[4] - 40),
    };
  }
  const idx = (stage - 1) as StageIdx;
  return {
    world, stage,
    layout: generateClassicLayout(t.density[idx], world * 10 + stage),
    enemyCount: t.enemies[idx],
    timeLimit: t.timeLimit[idx],
  };
}

export const LEVELS: LevelMap[] = (() => {
  const out: LevelMap[] = [];
  for (let w = 1; w <= 10; w++) {
    for (let s = 1; s <= 6; s++) out.push(makeLevel(w, s));
  }
  return out;
})();

export function getLevel(world: number, stage: number): LevelMap {
  return LEVELS.find(l => l.world === world && l.stage === stage) ?? LEVELS[0];
}

export function getNextLevel(world: number, stage: number): { world: number; stage: number } | null {
  const idx = LEVELS.findIndex(l => l.world === world && l.stage === stage);
  if (idx < 0 || idx + 1 >= LEVELS.length) return null;
  const n = LEVELS[idx + 1];
  return { world: n.world, stage: n.stage };
}

export function isBossLevel(_world: number, stage: number): boolean {
  return stage === 6;
}

export function getWorldTheme(world: number): string {
  return WORLD_TUNINGS[world]?.themeName ?? 'Necunoscut';
}
